import { useState } from 'react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { ArrowLeft, LogOut, User, Radio } from 'lucide-react';
import { Channel, Program } from '../data/mockData';
import { EventsTab } from './EventsTab';
import { ProgramsTab } from './ProgramsTab';
import { FilesTab } from './FilesTab';
import { NotificationBell } from './NotificationBell';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './ui/breadcrumb';

interface ChannelWorkspaceScreenProps {
  channel: Channel;
  stationName: string;
  currentUsername: string;
  onBack: () => void;
  onLogout: () => void;
  onSaveProgram: (program: Omit<Program, 'id' | 'channelId'>) => Promise<void>;
  onUpdateProgram: (
    programId: string | number,
    program: Omit<Program, 'id' | 'channelId'>,
    editMode?: 'single' | 'series'
  ) => Promise<void>;
  onDeleteProgram: (programId: string | number) => Promise<void>;
  onUploadTrack: (file: File) => Promise<void>;
  onDeleteTrack: (trackId: string | number) => void;
}

export function ChannelWorkspaceScreen({
  channel,
  stationName,
  currentUsername,
  onBack,
  onLogout,
  onSaveProgram,
  onUpdateProgram,
  onDeleteProgram,
  onUploadTrack,
  onDeleteTrack,
}: ChannelWorkspaceScreenProps) {
  const formatChannelName = (name: string) =>
    name ? name.charAt(0).toUpperCase() + name.slice(1) : name;
  const displayName = currentUsername || 'User';
  const [activeTab, setActiveTab] = useState<'events' | 'programs' | 'uploads'>('events');

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-950">
        <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button
              variant="ghost"
              onClick={onBack}
              className="gap-1 sm:gap-2 text-gray-300 hover:text-white hover:bg-gray-800 px-2 sm:px-4"
            >
              <ArrowLeft className="size-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="h-6 w-px bg-gray-700 hidden sm:block" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    className="text-gray-400 hover:text-white cursor-pointer"
                    onClick={onBack}
                  >
                    {stationName}
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="text-gray-500" />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-white font-bold">
                    {formatChannelName(channel.name)}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <NotificationBell channelId={channel.id as number} variant="dark" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 text-gray-300 hover:text-white hover:bg-gray-800">
                  <Avatar className="size-8">
                    <AvatarFallback className="bg-blue-600 text-white">
                      <User className="size-4" />
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline">{displayName}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-gray-900 border-gray-800">
                <DropdownMenuItem onClick={onLogout} className="text-gray-300 cursor-pointer hover:bg-gray-800 focus:bg-gray-800">
                  <LogOut className="mr-2 size-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-gray-800 bg-gray-900">
        <div className="px-4 sm:px-6 flex gap-1 sm:gap-2 overflow-x-auto">
          <button
            onClick={() => setActiveTab('events')}
            className={`px-3 sm:px-6 py-3 sm:py-4 font-bold text-base sm:text-lg transition-colors relative whitespace-nowrap ${
              activeTab === 'events'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            Events
            {activeTab === 'events' && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-500" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('programs')}
            className={`px-3 sm:px-6 py-3 sm:py-4 font-bold text-base sm:text-lg transition-colors relative whitespace-nowrap ${
              activeTab === 'programs'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            Programs
            {activeTab === 'programs' && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-500" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('uploads')}
            className={`px-3 sm:px-6 py-3 sm:py-4 font-bold text-base sm:text-lg transition-colors relative whitespace-nowrap ${
              activeTab === 'uploads'
                ? 'text-white'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            Files
            {activeTab === 'uploads' && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-500" />
            )}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <main className="flex-1">
        {activeTab === 'events' ? (
          <EventsTab
            channelId={channel.id}
          />
        ) : activeTab === 'programs' ? (
          <ProgramsTab
            channelId={channel.id}
            onSaveProgram={onSaveProgram}
            onUpdateProgram={onUpdateProgram}
            onDeleteProgram={onDeleteProgram}
            onUploadTrack={onUploadTrack}
          />
        ) : (
          <FilesTab
            channelId={channel.id}
            stationName={stationName}
            channelName={channel.name}
            onUploadTrack={onUploadTrack}
            onDeleteTrack={onDeleteTrack}
          />
        )}
      </main>
    </div>
  );
}